import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import { PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/constants'
import ProductAvailability from 'Services/ProductManagement/Features/Product/Components/ProductAvailability/ProductAvailability'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import { ProductTabType } from 'Services/ProductManagement/Features/Product/productSlice'
import useTypedSelector from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import { PRODUCT_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface ProductDrawerProps {
  open: boolean
  handleClose: () => void
  handleProductDelete: () => void
}

export function ProductDrawer({
  open,
  handleClose,
  handleProductDelete
}: Readonly<ProductDrawerProps>) {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/products`
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const drawerTabFromParams =
    searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) ?? ProductTabType.DETAILS
  const { confirm } = useConfirm()
  const { singleProduct, singleLoading: singleProductLoading } = useTypedSelector(
    state => state.product
  )
  const { confirmForUnsavedChanges } = useUnsavedChanges()

  const previousLocation = usePreviousLocation(
    `${baseUrl}?${SELECTED_PRODUCT_PARAMETER_KEY}=${singleProduct?.id}`
  )

  const handleDrawerTabChange = (_event: React.SyntheticEvent, newValue: ProductTabType) => {
    confirmForUnsavedChanges(() => {
      searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
      setSearchParams(searchParams)
    })
  }

  const handleDrawerClose = () => {
    confirmForUnsavedChanges(() => {
      handleClose()
    })
  }

  const isSingleProductLoading = singleProductLoading === 'pending'

  return (
    <DrawerRight
      open={open}
      onClose={handleDrawerClose}
      topBar={
        <Box>
          {singleProduct && (
            <>
              <DeleteButton
                sx={{ mr: 1 }}
                size="small"
                onClick={() =>
                  confirm({
                    title: t`Are you sure?`,
                    message: t`Are you sure you want to delete the product?`,
                    handleConfirm: () => {
                      handleProductDelete()
                    }
                  })
                }
              >{t`Delete`}</DeleteButton>
              <CustomButton
                onClick={() => navigate(`${baseUrl}/${singleProduct.id}/edit`)}
                size="small"
                startIcon={<EditIcon />}
              >{t`Edit`}</CustomButton>
            </>
          )}
        </Box>
      }
    >
      <TabContext value={drawerTabFromParams}>
        <TabList onChange={handleDrawerTabChange} aria-label={t`Product tab list`}>
          <Tab label={t`Product details`} value={ProductTabType.DETAILS} />
          <Tab label={t`Availability`} value={ProductTabType.AVAILABILITY} />
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={ProductTabType.DETAILS}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && <ProductDetails product={singleProduct} />}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={ProductTabType.AVAILABILITY}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && (
              <ProductAvailability
                product={singleProduct}
                successBaseUrl={previousLocation}
                handleClose={handleDrawerClose}
              />
            )}
          </WithLoader>
        </TabPanel>
      </TabContext>
    </DrawerRight>
  )
}
